import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormatOptions, ValueFormat, formatValue } from './chartFormat';
import { Highcharts, HighchartsReact, chartTheme } from './chartEngine';
import { CHART_COLORS, CHART_HEIGHT, CHART_MARKS, ChartRole, ChartSize, colorOfRole } from './chartTokens';
import { escapeHtml, squareKey, tooltipCard, tooltipRow } from './chartTooltip';
import ChartMessage from './ChartMessage';

// Bars that compare categories without dates: the lowest price per channel, the ADR per month,
// the difference to the compset per weekday.
// The role of a bar series selects its colour, as in LineChart: 'own' is blue-600, 'compset' is
// gray-500, 'series' takes the next series slot. Each bar of one series has the same colour: the
// length shows the value, the colour shows only the series.
// Horizontal (default) suits long category names; the height follows the number of categories.
// Vertical suits a short ordered list (months, weekdays); its height comes from `size`.
// One series shows its value at the end of each bar. Two series or more get a legend and no values
// on the bars. A pointer on a category, or the arrow keys after Tab, shows one tooltip with every
// value of the category. A negative value goes to the other side of the zero line.
// A null value has no bar. With no value at all, the chart shows emptyLabel.
// Stacked: the series of one category sit on top of each other, so the height is the total and each
// part is one series (room nights per month by group). The tooltip adds the total. No value on the bars.
// Side by side stacks: with `stacked`, a series with a `stack` name sits on the bar of that stack, and the stacks of
// one category stand side by side (this year and 2025, each by group). A series that repeats another one in a second
// stack gives its id in `linkedTo`: it takes that colour and has no legend item of its own (a click on the legend item
// hides both). `stackLabels` puts a short name above each stack ("’25"). The tooltip shows the stack under the pointer.
// A band marks a range of categories (for example the months before the first import, "No data"), and a
// marker line marks a place between categories (for example "today").
// onBarClick: a click on a bar (or Enter on it after Tab) gives the index of its category, for example to highlight that row
// in a table; the bars show a pointer. tooltipLines: more lines under the values of the tooltip of a category, as text
// that the caller writes ("Share · 19.5 % · 20.0 %", "Δ% · +5.6 %").
// The chart knows nothing about the data: the caller gives the categories, the values and the labels.

export type BarChartSeries = {
	id: string,
	label: string,
	// One value per category. null = no value (no bar).
	values: (number | null)[],
	// Default 'series'.
	role?: ChartRole,
	// With `stacked`: the bar of the category that this series sits on. Series without a stack share one bar.
	stack?: string,
	// The id of a series that this one repeats in another stack: the same colour, no legend item of its own.
	linkedTo?: string,
};

type Props = {
	categories: string[],
	series: BarChartSeries[],
	// money: €142 · percent: a fraction, 0.714 → 71% · number: 1,284
	format: ValueFormat,
	// An ISO 4217 code for 'money'. Default 'EUR'.
	currency?: string,
	// The decimals of the values on the bars and in the tooltip. The axis shows no decimals. Default 0.
	decimals?: number,
	// Default 'horizontal'.
	orientation?: 'horizontal' | 'vertical',
	// Vertical bars only: the total height, small 200 px, normal 280 px, large 360 px.
	// Horizontal bars: the height follows the number of categories.
	size?: ChartSize,
	// The name of the chart for a screen reader.
	ariaLabel: string,
	// The text when no bar has a value.
	emptyLabel?: string,
	// The series of a category sit on top of each other. Default false.
	stacked?: boolean,
	// Ranges of categories with a light fill and a label, by category index (both ends included).
	bands?: BarChartBand[],
	// Marker lines at a place on the category axis: an index, 3.5 = between the 4th and the 5th category.
	markers?: BarChartMarker[],
	// Vertical bars only: the category labels are never cut or shortened ("29 Dec ’25", not "29 Dec…"),
	// and the plot gets 16 px on the left for the first rotated label. Default false: long category
	// names of horizontal bars keep their ellipsis.
	wholeLabels?: boolean,
	// The end of the value axis, for example 1 for one bar of shares that ends at 100 %. Default: from the values.
	max?: number,
	// With side by side stacks: a short name above each stack, by stack name ({ '2025': '’25' }).
	stackLabels?: Record<string, string>,
	// A click on a bar: the index of its category.
	onBarClick?: (categoryIndex: number) => void,
	// More lines at the end of the tooltip of a category: a label and a value, as text.
	tooltipLines?: (categoryIndex: number) => { label: string, value: string }[],
};

export type BarChartBand = { from: number, to: number, label: string };
export type BarChartMarker = { at: number, label: string };

const BAR = 18; // the thickness of one bar in a horizontal group, in px
const MAX_BAR = 24; // never thicker
const LEGEND_H = 30;
const AXIS_H = 34;

const hasValue = (v: number | null | undefined): v is number => v !== null && v !== undefined && !Number.isNaN(v);

// The lines of the legend at a width. Highcharts puts the items in columns as wide as the widest item: its key (16 px +
// 5 px) and its label (about 6.5 px per character of 12 px text), 16 px apart (chartTheme), inside the spacing of the
// chart (about 30 px). An unknown width gives one line.
export const legendLines = (labels: string[], width: number): number => {
	if (width <= 0 || labels.length === 0) return 1;
	const item = Math.max(...labels.map(label => 21 + Math.ceil(label.length * 6.5)));
	const perLine = Math.max(1, Math.floor((width - 30 + 16) / (item + 16)));
	const all = labels.reduce((sum, label) => sum + 21 + Math.ceil(label.length * 6.5), 0) + 16 * (labels.length - 1);
	return all <= width - 30 ? 1 : Math.ceil(labels.length / perLine);
};
const LEGEND_LINE = 18;

const BarChart: React.FunctionComponent<Props> = ({
	categories, series, format, currency, decimals = 0, orientation = 'horizontal', size = 'normal', ariaLabel,
	emptyLabel = 'No data for this period.', stacked = false, bands = [], markers = [], wholeLabels = false, max, stackLabels,
	onBarClick, tooltipLines,
}) => {
	const isHorizontal = orientation === 'horizontal';
	// The width of the chart: a long legend takes more lines on a narrow screen, and a horizontal chart grows by them.
	const box = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(0);
	const isEmpty = series.every(s => !s.values.some(hasValue));
	// ROO2-262 (app-only change; the design system must take it): an empty chart renders no box, so the effect runs again
	// when the data comes and the box mounts; else the width stays 0 and a wrapped legend overlaps the bars.
	useEffect(() => {
		const node = box.current;
		if (node === null || typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(entries => setWidth(Math.round(entries[0].contentRect.width)));
		observer.observe(node);
		return () => observer.disconnect();
	}, [isEmpty]);
	const isWhole = wholeLabels && !isHorizontal;
	const legendSeries = series.filter(s => s.linkedTo === undefined);
	const hasLegend = legendSeries.length >= 2;
	const showValues = series.length === 1 && !stacked;
	const stacks = stacked ? [...new Set(series.map(s => s.stack ?? ''))] : [];
	// The series of a stacked group sit on one bar, so a stacked category needs the height of one bar per stack.
	const barsPerCategory = stacked ? stacks.length : series.length;
	const legendHeight = hasLegend ? LEGEND_H + (legendLines(legendSeries.map(x => x.label), width) - 1) * LEGEND_LINE : 0;
	const height = isHorizontal
		? categories.length * Math.max(32, barsPerCategory * (BAR + 4) + 12) + AXIS_H + legendHeight
		: CHART_HEIGHT[size];

	const options = useMemo<Highcharts.Options>(() => {
		const valueOptions: FormatOptions = { currency, decimals };
		const axisOptions: FormatOptions = { currency, decimals: 0 };

		let seriesIndex = 0;
		const own = new Map<string, string>();
		series.forEach(s => {
			if (s.linkedTo !== undefined) return;
			const role = s.role ?? 'series';
			own.set(s.id, colorOfRole(role, seriesIndex));
			if (role === 'series') seriesIndex += 1;
		});
		// A linked series takes the colour of the series that it repeats.
		const colors = series.map(s => own.get(s.linkedTo ?? s.id) ?? colorOfRole(s.role ?? 'series', 0));

		const bars: Highcharts.SeriesOptionsType[] = series.map((s, i) => ({
			type: isHorizontal ? 'bar' : 'column',
			id: s.id,
			name: s.label,
			color: colors[i],
			legendIndex: i,
			...(stacked && s.stack !== undefined ? { stack: s.stack } : {}),
			...(s.linkedTo !== undefined ? { linkedTo: s.linkedTo } : {}),
			data: s.values.map(v => hasValue(v) ? v : null),
			// Over the bands (zIndex 1).
			zIndex: 2,
		}));

		const barOptions: Highcharts.PlotColumnOptions = {
			// A rounded data end, square at the zero line.
			borderRadius: { radius: 4, scope: stacked ? 'stack' : 'point', where: 'end' },
			stacking: stacked ? 'normal' : undefined,
			borderWidth: 0,
			maxPointWidth: MAX_BAR,
			// A small white gap between the bars of one group.
			pointPadding: stacked ? 0.12 : (series.length > 1 ? 0.1 : 0.06),
			groupPadding: isHorizontal ? 0.16 : 0.2,
			states: { hover: { brightness: -0.12 } },
			...(onBarClick !== undefined ? {
				cursor: 'pointer',
				point: { events: { click: function (this: Highcharts.Point) { onBarClick(Number(this.index ?? this.x)); } } },
			} : {}),
			dataLabels: {
				enabled: showValues,
				inside: false,
				crop: false,
				overflow: 'allow',
				style: {
					color: CHART_COLORS.textPrimary,
					fontSize: `${CHART_MARKS.fontSize}px`,
					fontWeight: '600',
					textOutline: 'none',
				},
				formatter: function (this: Highcharts.Point) {
					return hasValue(this.y) ? formatValue(this.y, format, valueOptions) : '';
				},
			},
		};

		// The last label of the value axis sits half over the right edge of the plot: a long label ("€110,000") needs more
		// room than the 20 px of a short one ("€120"), else the edge cuts it. The width of the label is estimated from the
		// largest bar (a stack counts its parts), 7 px per character.
		const largest = Math.max(0, ...categories.map((_, i) => stacked
			? series.reduce((sum, s) => sum + Math.max(0, s.values[i] ?? 0), 0)
			: Math.max(0, ...series.map(s => s.values[i] ?? 0))));
		const lastLabel = formatValue(max ?? largest * 1.2, format, axisOptions);
		const spacingRight = isHorizontal ? Math.max(20, Math.ceil(lastLabel.length * 3.5) + 4) : 20;

		const sideBySide = stacked && stacks.length > 1;
		const chartOptions: Highcharts.Options = {
			chart: { height, spacingRight, ...(isWhole ? { spacingLeft: 16 } : {}) },
			// No pages in the legend: a horizontal chart grows by the lines of its legend.
			legend: { enabled: hasLegend, navigation: { enabled: false } },
			xAxis: {
				type: 'category',
				categories,
				crosshair: false,
				lineColor: CHART_COLORS.axisLine,
				plotBands: bands.map(band => ({
					from: band.from - 0.5,
					to: band.to + 0.5,
					color: CHART_COLORS.noData,
					// The band sits over the grid lines (gridZIndex 0) and under the bars (zIndex 2): no grid line runs
					// through the band or its label.
					zIndex: 1,
					label: { text: band.label, style: { color: CHART_COLORS.textSecondary, fontSize: `${CHART_MARKS.fontSize}px` }, verticalAlign: 'middle', y: 4 },
				})),
				plotLines: markers.map(marker => ({
					value: marker.at,
					color: CHART_COLORS.marker,
					width: 1,
					dashStyle: 'Dash',
					zIndex: 5,
					label: { text: marker.label, rotation: 0, align: 'center', y: -4, style: { color: CHART_COLORS.textSecondary, fontSize: '11px' } },
				})),
				labels: { style: isWhole
					? { color: CHART_COLORS.textSecondary, textOverflow: 'none', whiteSpace: 'nowrap' }
					: { color: CHART_COLORS.textSecondary } },
			},
			yAxis: {
				// The first series sits at the bottom of a stack, in the order of the legend.
				reversedStacks: false,
				stackLabels: {
					// Only when each stack has room for its name (24 px); on a narrow chart the tooltip names the stack.
					enabled: sideBySide && stackLabels !== undefined && width / (categories.length * stacks.length) >= 24,
					// The names of neighbour stacks sit close together: keep them all.
					allowOverlap: true,
					crop: false,
					overflow: 'allow',
					style: { color: CHART_COLORS.textSecondary, fontSize: '10px', fontWeight: '500', textOutline: 'none' },
					formatter: function (this: Highcharts.StackItemObject) {
						return stackLabels?.[String((this as unknown as { stack?: string }).stack)] ?? '';
					},
				},
				// The grid lines under the bands (see xAxis.plotBands).
				gridZIndex: 0,
				// A bar grows from zero.
				softMin: 0,
				softMax: 0,
				// Room for the value at the end of a bar, on both sides of zero.
				maxPadding: max !== undefined ? 0 : (showValues ? 0.12 : 0.05),
				...(max !== undefined ? { max, endOnTick: false } : {}),
				minPadding: showValues ? 0.12 : 0.05,
				tickPixelInterval: isHorizontal ? 96 : 56,
				plotLines: [{ value: 0, width: 1, color: CHART_COLORS.axisLine, zIndex: 3 }],
				labels: {
					// The last label can pass the plot edge: show it whole, never as '€…'.
					overflow: 'allow',
					style: { textOverflow: 'none', whiteSpace: 'nowrap' },
					formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
						// A percent tick that is not a whole percent (2.5 %) keeps one decimal: "3%" would name the wrong value.
						const value = Number(this.value);
						const decimals = format === 'percent' && Math.abs(value * 100 - Math.round(value * 100)) > 1e-9 ? 1 : 0;
						return formatValue(value, format, { ...axisOptions, decimals });
					},
				},
			},
			plotOptions: { bar: barOptions, column: barOptions },
			tooltip: {
				// Side by side stacks: the tooltip and the hover belong to the bar under the pointer, not to the whole category.
				...(sideBySide ? { shared: false } : {}),
				formatter: function (this: Highcharts.Point) {
					const i = Number(this.index ?? this.x);
					const category = categories[i];
					if (category === undefined) return false;
					const chart = this.series.chart;
					// Side by side stacks: only the stack under the pointer.
					const stack = sideBySide ? (this.series.options as { stack?: string }).stack : undefined;
					const visible = series
						.map((s, k) => ({ s, k, visible: chart.series.find(c => c.options.id === s.id)?.visible ?? true }))
						.filter(r => r.visible && (stack === undefined || r.s.stack === stack));
					const rows = (stacked ? [...visible].reverse() : visible)
						.map(r => tooltipRow(squareKey(colors[r.k]), formatValue(r.s.values[i], format, valueOptions), r.s.label));
					if (stacked && visible.length > 1) {
						const total = visible.reduce((sum, r) => sum + (hasValue(r.s.values[i]) ? r.s.values[i] as number : 0), 0);
						rows.push(tooltipRow('', formatValue(total, format, valueOptions), 'Total'));
					}
					(tooltipLines?.(i) ?? []).forEach(line => rows.push(tooltipRow('', escapeHtml(line.value), line.label)));
					const title = stack === undefined ? category : `${category} · ${stackLabels?.[stack] ?? stack}`;
					return rows.length === 0 ? false : tooltipCard(title, rows);
				},
			},
			accessibility: {
				description: ariaLabel,
				point: {
					descriptionFormatter: (point: Highcharts.Point): string =>
						`${categories[point.index] ?? ''}, ${formatValue(point.y, format, valueOptions)}. ${point.series.name}.`,
				},
			},
			series: bars,
		};
		return Highcharts.merge(chartTheme(height), chartOptions);
	}, [categories, series, format, currency, decimals, isHorizontal, height, hasLegend, showValues, ariaLabel, stacked, bands, markers, isWhole, max, stackLabels, stacks.length, width, onBarClick, tooltipLines]);

	if (isEmpty) return <ChartMessage height={isHorizontal ? CHART_HEIGHT.small : height} ariaLabel={ariaLabel}>{emptyLabel}</ChartMessage>;

	return <div ref={box} role="figure" aria-label={ariaLabel} className="w-full">
		<HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { width: '100%' } }} />
	</div>;
};

export default BarChart;
